import Icon from "@react-native-vector-icons/material-design-icons";
import theme from "assets/theme";
import { uploadFileWeb } from "helpers/shared.helpers";
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { StyleSheet, Text, View } from "react-native";
import FileService from "services/File.service";
import { ConfirmationContext } from "web/context/Confirmation.context";
import CustomModal from "webComponentsFormElements/CustomModal.component";
import PressableContainer from "webComponentsFormElements/PressableContainer.component";
import FileUpload from "webComponentsFormElements/inputs/FileUpload.component";
import LabelInput from "webComponentsFormElements/inputs/LabelInput.component";
import RadioSelect from "webComponentsFormElements/inputs/RadioSelect.component";
import { OrganizationContext } from "webContext/Organization.context";
import { ToastContext } from "webContext/Toast.context";

const classes = StyleSheet.create({
  root: {
    flexDirection: "row",
    gap: 100,
  },
  sideContainer: {
    gap: 10,
  },
  label: {
    fontWeight: theme.font.weight.medium,
    color: theme.color.darkGrey,
    margin: 10,
  },
  documentContainer: {
    alignItems: "center",
    gap: 10,
  },
  downloadContainer: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
  },
  noneText: {
    fontStyle: "italic",
  },
  fileTypeHint: {
    fontSize: 12,
    margin: 5,
    fontStyle: "italic",
    fontWeight: theme.font.weight.regular,
    color: theme.color.darkGrey,
  },
});

export default function EditResourceModal(props) {
  const { resource, open, onClose } = props;

  const { organizationColor, upsertOrganizationResource } =
    useContext(OrganizationContext);
  const { showToast } = useContext(ToastContext);
  const { showConfirmation } = useContext(ConfirmationContext);
  const [editedResource, setEditedResource] = useState({ title: "" });
  const [editedResourceType, setEditedResourceType] = useState("file");
  const [editedResourceFile, setEditedResourceFile] = useState(null);
  const [editedResourceFileBlob, setEditedResourceFileBlob] = useState(null);
  const [
    editedResourceFileIsAutoDownloaded,
    setEditedResourceFileIsAutoDownloaded,
  ] = useState(false);

  const file = editedResourceFile && editedResource._associations?.file;
  const initialResource = useRef();
  const initialType = useRef();
  const initialFile = useRef();
  const initialFileIsAutoDownloaded = useRef();

  useEffect(() => {
    if (resource) {
      setEditedResource(resource);
      setEditedResourceType(resource.fileId ? "file" : "link");
      setEditedResourceFile(resource.fileId);
      setEditedResourceFileBlob(null);
      setEditedResourceFileIsAutoDownloaded(
        !!resource._associations?.file?.isDownloadable,
      );
      if (open) {
        initialResource.current = resource;
        initialType.current = resource.fileId ? "file" : "link";
        initialFile.current = resource.fileId;
        initialFileIsAutoDownloaded.current =
          !!resource._associations?.file?.isDownloadable;
      }
    } else {
      setEditedResource({ title: "" });
      setEditedResourceType("file");
      setEditedResourceFile(null);
      setEditedResourceFileBlob(null);
      setEditedResourceFileIsAutoDownloaded(false);
    }
  }, [open, resource]);

  useEffect(() => {
    if (!open) {
      setEditedResource({ title: "" });
      setEditedResourceType("file");
      setEditedResourceFile(null);
      setEditedResourceFileBlob(null);
      setEditedResourceFileIsAutoDownloaded(false);
    }
  }, [open]);

  const stateClasses = useMemo(
    () =>
      StyleSheet.create({
        downloadLink: {
          color: editedResourceFile ? organizationColor : theme.color.darkGrey,
          paddingBottom: 2,
        },
      }),
    [editedResourceFile, organizationColor],
  );

  const onFileDownload = useCallback(async () => {
    if (editedResourceFileBlob) {
      const url = window.URL.createObjectURL(editedResourceFileBlob);

      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download =
        editedResourceFileBlob.name ||
        file?.fileName ||
        `resource-${editedResourceFile}`;

      document.body.appendChild(anchor);
      anchor.click();

      anchor.remove();
      window.URL.revokeObjectURL(url);
    } else if (editedResource.signedFileUrl) {
      window.open(editedResource.signedFileUrl, "_blank");
    }
  }, [
    editedResource.signedFileUrl,
    editedResourceFile,
    editedResourceFileBlob,
    file?.fileName,
  ]);

  // Track if form is valid
  const isValid = useMemo(
    () =>
      editedResource.title.length > 0 &&
      (editedResourceType === "file"
        ? !!editedResourceFile
        : editedResource.url?.length > 0),
    [
      editedResource.title,
      editedResourceType,
      editedResourceFile,
      editedResource.url,
    ],
  );

  // Track if form has unsaved changes
  const hasUnsavedChanges = useMemo(
    () =>
      editedResource.title !== initialResource.current?.title ||
      editedResource.url !== initialResource.current?.url ||
      editedResourceFile !== initialFile.current ||
      editedResourceType !== initialType.current ||
      editedResourceFileIsAutoDownloaded !==
        initialFileIsAutoDownloaded.current,
    [
      editedResource.title,
      editedResource.url,
      editedResourceFile,
      editedResourceType,
      editedResourceFileIsAutoDownloaded,
    ],
  );

  // canSave: valid AND changed
  const canSave = isValid && hasUnsavedChanges;

  const onSave = useCallback(async () => {
    try {
      if (editedResourceType === "file") {
        delete editedResource.url;
      } else {
        setEditedResourceFile(null);
        setEditedResourceFileBlob(null);
      }

      let fileId = editedResourceFile;
      if (editedResourceFile && editedResourceFile !== editedResource.fileId) {
        const res = await uploadFileWeb(
          editedResourceFileBlob,
          2,
          editedResourceFileIsAutoDownloaded,
        );
        fileId = res.fileId;
      } else if (
        resource &&
        editedResourceFileIsAutoDownloaded !==
          !!resource._associations.file?.isDownloadable
      ) {
        await FileService.updateFile({
          fileId: editedResource.fileId,
          isDownloadable: editedResourceFileIsAutoDownloaded,
        });
      }

      const data = {
        ...editedResource,
        fileId,
      };

      await upsertOrganizationResource(data);

      showToast(`Successfully saved ${editedResource.title}.`, "success");

      onClose();
    } catch (error) {
      showToast(`Failed to save ${editedResource.title}.`, "error");
      if (__DEV__) {
        console.error(error);
      }
    }
  }, [
    resource,
    editedResourceType,
    editedResource,
    editedResourceFile,
    editedResourceFileBlob,
    editedResourceFileIsAutoDownloaded,
    onClose,
    showToast,
    upsertOrganizationResource,
  ]);

  return (
    <CustomModal
      title={resource ? "Edit Resource" : "Create Resource"}
      open={open}
      onClose={() => {
        if (hasUnsavedChanges) {
          showConfirmation({
            prompt:
              "You have unsaved changes. Are you sure you want to discard them?",
            onConfirm: () => {
              onClose();
            },
          });
        } else {
          onClose();
        }
      }}
      canSave={canSave}
      onSave={onSave}
    >
      <View style={classes.root}>
        <View style={classes.sideContainer}>
          <LabelInput
            label="Name"
            value={editedResource.title}
            onChange={(event) =>
              setEditedResource((oldResource) => ({
                ...oldResource,
                title: event.target.value,
              }))
            }
            required
          />
          <RadioSelect
            label="Type"
            options={[
              { value: "link", label: "Link" },
              { value: "file", label: "File" },
            ]}
            value={editedResourceType}
            onChange={setEditedResourceType}
          />
        </View>
        <View style={classes.sideContainer}>
          {editedResourceType === "file" ? (
            <>
              <RadioSelect
                label="Automatically download"
                tooltip='If set to "yes", this will be downloaded and stored to user devices automatically'
                options={[
                  { value: "no", label: "No" },
                  { value: "yes", label: "Yes" },
                ]}
                value={editedResourceFileIsAutoDownloaded ? "yes" : "no"}
                onChange={(value) =>
                  setEditedResourceFileIsAutoDownloaded(value === "yes")
                }
              />
              <View>
                <Text style={classes.label}>Attached document</Text>
                <Text style={classes.fileTypeHint}>
                  Accepted file types: .docx, .pdf, .txt
                </Text>
                <View style={classes.documentContainer}>
                  <FileUpload
                    file={editedResourceFile}
                    setFile={setEditedResourceFile}
                    setFileBlob={setEditedResourceFileBlob}
                    isDocumentUpload
                    accept=".docx,.pdf,.txt"
                  />
                  <PressableContainer
                    style={classes.downloadContainer}
                    onPress={onFileDownload}
                  >
                    <Icon
                      name="download"
                      color={
                        editedResourceFile
                          ? organizationColor
                          : theme.color.darkGrey
                      }
                      size={18}
                    />
                    <Text
                      style={[
                        stateClasses.downloadLink,
                        !editedResourceFile && classes.noneText,
                      ]}
                    >
                      {editedResourceFileBlob?.name ||
                        file?.fileName ||
                        "No file attached"}
                    </Text>
                  </PressableContainer>
                </View>
              </View>
            </>
          ) : (
            <LabelInput
              label="Link"
              value={editedResource.url}
              onChange={(event) =>
                setEditedResource((oldResource) => ({
                  ...oldResource,
                  url: event.target.value,
                }))
              }
              required
            />
          )}
        </View>
      </View>
    </CustomModal>
  );
}
